import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Heading, LoadingSpinner, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingService } from '../../services/marketing'
import type { MarketingGame, MarketingGameSession } from '../../types/marketing'
import {
  BOARD_SIZE,
  buildOrderSquares,
  computePath,
  formatGameTime,
  generatePuzzle,
  getElapsedMs,
  rankingPlaceLabel,
} from './gamePuzzle'

type Screen = 'loading' | 'empty' | 'start' | 'game' | 'result' | 'played' | 'abandoned'

export interface GameHudStats {
  visible: boolean
  /** Tempo decorrido em milissegundos */
  elapsedMs: number
  found: number
  total: number
}

interface GameJogarTabProps {
  onHudChange?: (stats: GameHudStats) => void
  onShareResult?: (text: string) => void
}

const GameJogarTab: React.FC<GameJogarTabProps> = ({ onHudChange, onShareResult }) => {
  const { user, profile } = useAuthContext()
  const { logCreate, logUpdate } = useUserLogs()

  const [screen, setScreen] = useState<Screen>('loading')
  const [error, setError] = useState<string | null>(null)
  const [game, setGame] = useState<MarketingGame | null>(null)
  const [existingSession, setExistingSession] = useState<MarketingGameSession | null>(null)
  const [leaderboard, setLeaderboard] = useState<MarketingGameSession[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)

  const [grid, setGrid] = useState<string[][] | null>(null)
  const [foundWords, setFoundWords] = useState<Record<string, boolean>>({})
  const [foundOrder, setFoundOrder] = useState<number[]>([])
  const [elapsedMs, setElapsedMs] = useState(0)
  const [resultMs, setResultMs] = useState(0)
  const [resultOrder, setResultOrder] = useState<number[]>([])
  const [resultSaved, setResultSaved] = useState(false)
  const [starting, setStarting] = useState(false)

  const startTimeRef = useRef<number | null>(null)
  const timerRef = useRef<number | null>(null)
  const boardRef = useRef<HTMLDivElement | null>(null)
  const isSelectingRef = useRef(false)
  const selectStartRef = useRef<[number, number] | null>(null)
  const [selectPath, setSelectPath] = useState<Array<[number, number]>>([])
  const foundCellsRef = useRef<Set<string>>(new Set())
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set())

  const foundWordsRef = useRef<Record<string, boolean>>({})
  const foundOrderRef = useRef<number[]>([])
  const sessionIdRef = useRef<string | null>(null)
  const screenRef = useRef<Screen>('loading')
  const completedRef = useRef(false)
  const elapsedMsRef = useRef(0)
  const gameTitleRef = useRef('')

  const userName = profile?.name || user?.email || 'Jogador'
  const questions = game?.questions ?? []
  const totalWords = questions.length
  const foundCount = Object.keys(foundWords).length

  useEffect(() => {
    if (!onHudChange) return
    onHudChange({
      visible: screen === 'game' || screen === 'result',
      elapsedMs: screen === 'result' ? resultMs : elapsedMs,
      found: foundCount,
      total: totalWords,
    })
  }, [onHudChange, screen, elapsedMs, resultMs, foundCount, totalWords])

  useEffect(() => {
    return () => {
      onHudChange?.({ visible: false, elapsedMs: 0, found: 0, total: 0 })
    }
  }, [onHudChange])

  useEffect(() => {
    screenRef.current = screen
  }, [screen])

  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  useEffect(() => {
    gameTitleRef.current = game?.title || ''
  }, [game?.title])

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startTimerNow = useCallback(() => {
    stopTimer()
    const now = Date.now()
    startTimeRef.current = now
    elapsedMsRef.current = 0
    setElapsedMs(0)
    timerRef.current = window.setInterval(() => {
      const next = getElapsedMs(startTimeRef.current)
      elapsedMsRef.current = next
      setElapsedMs(next)
    }, 50)
  }, [stopTimer])

  const readElapsedMs = useCallback(() => {
    if (startTimeRef.current == null) return elapsedMsRef.current
    const next = getElapsedMs(startTimeRef.current)
    elapsedMsRef.current = next
    return next
  }, [])

  const abandonCurrentSession = useCallback(async () => {
    const id = sessionIdRef.current
    if (!id || completedRef.current) return
    if (screenRef.current !== 'game') return

    stopTimer()
    const elapsed = readElapsedMs()
    const order = foundOrderRef.current.slice()

    try {
      const abandoned = await marketingService.abandonGameSession(id, {
        time_seconds: elapsed,
        word_order: order,
      })
      if (abandoned) {
        setExistingSession(abandoned)
        void logUpdate(
          'marketing_game_session',
          abandoned.id,
          `Não concluiu caça-palavras: ${gameTitleRef.current || 'jogo'}`,
          undefined,
          { time_seconds: elapsed, word_order: order, status: 'abandoned' }
        )
      }
    } catch {
      /* best-effort ao sair */
    }
  }, [logUpdate, readElapsedMs, stopTimer])

  useEffect(() => {
    return () => {
      void abandonCurrentSession()
      stopTimer()
    }
    // abandon usa refs; não pode reexecutar a cada tick do cronômetro
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onLeave = () => {
      void abandonCurrentSession()
    }
    window.addEventListener('pagehide', onLeave)
    return () => {
      window.removeEventListener('pagehide', onLeave)
    }
  }, [abandonCurrentSession])

  const loadLeaderboard = useCallback(async (gameId: string) => {
    try {
      const list = await marketingService.listGameSessions(gameId)
      setLeaderboard(list)
    } catch {
      setLeaderboard([])
    }
  }, [])

  const loadActiveGame = useCallback(async () => {
    if (!user?.id) {
      setError('Usuário não autenticado.')
      setScreen('empty')
      return
    }
    try {
      setScreen('loading')
      setError(null)
      const active = await marketingService.getActiveGame()
      setGame(active)
      if (!active) {
        setScreen('empty')
        return
      }

      let session = await marketingService.getUserGameSession(active.id, user.id)

      // Sessão iniciada e não concluída = desistência (sem continuar)
      if (session && session.status === 'in_progress' && !session.completed_at) {
        const abandoned = await marketingService.abandonGameSession(session.id, {
          time_seconds: session.time_seconds ?? 0,
          word_order: session.word_order ?? [],
        })
        session = abandoned ?? { ...session, status: 'abandoned' as const }
        void logUpdate(
          'marketing_game_session',
          session.id,
          `Não concluiu caça-palavras: ${active.title}`,
          undefined,
          { status: 'abandoned', game_id: active.id }
        )
      }

      setExistingSession(session)
      await loadLeaderboard(active.id)

      if (!session) {
        setScreen('start')
        return
      }

      if (session.status === 'completed' || session.completed_at) {
        setResultMs(session.time_seconds ?? 0)
        setResultOrder(session.word_order ?? [])
        setScreen('played')
        return
      }

      setScreen('abandoned')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o jogo')
      setScreen('empty')
    }
  }, [loadLeaderboard, logUpdate, user?.id])

  useEffect(() => {
    if (screenRef.current === 'game' || screenRef.current === 'result') return
    void loadActiveGame()
  }, [loadActiveGame])

  const beginGame = async () => {
    if (!game || !user?.id || starting) return

    // Nunca retoma partida anterior — uma tentativa apenas
    if (existingSession) {
      setScreen(
        existingSession.status === 'completed' || existingSession.completed_at
          ? 'played'
          : 'abandoned'
      )
      return
    }

    try {
      setStarting(true)
      setError(null)
      setResultSaved(false)
      completedRef.current = false

      const session = await marketingService.createGameSession({
        game_id: game.id,
        user_id: user.id,
        user_name: userName,
      })
      setExistingSession(session)
      setSessionId(session.id)
      sessionIdRef.current = session.id
      void logCreate('marketing_game_session', session.id, `Iniciou caça-palavras: ${game.title}`, {
        game_id: game.id,
      })

      const puzzle = generatePuzzle(questions.map((q) => q.word))
      setGrid(puzzle.grid)
      foundWordsRef.current = {}
      foundOrderRef.current = []
      setFoundWords({})
      setFoundOrder([])
      foundCellsRef.current = new Set()
      setFoundCells(new Set())
      setSelectPath([])

      startTimerNow()
      setScreen('game')
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Não foi possível iniciar o jogo'
      setError(message)
      if (message.includes('já jogou') || message.includes('uma vez')) {
        await loadActiveGame()
      }
    } finally {
      setStarting(false)
    }
  }

  const finishGame = async (order: number[]) => {
    stopTimer()
    const elapsed = readElapsedMs()
    setResultMs(elapsed)
    setResultOrder(order)
    screenRef.current = 'result'
    setScreen('result')

    window.setTimeout(() => {
      document
        .getElementById('caca-palavras-resultado')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)

    if (completedRef.current) return
    completedRef.current = true

    const id = sessionIdRef.current
    if (!id || !game) return

    try {
      const completed = await marketingService.completeGameSession(id, {
        completed_at: new Date().toISOString(),
        time_seconds: elapsed,
        word_order: order,
      })
      setExistingSession(completed)
      setResultSaved(true)
      void logUpdate(
        'marketing_game_session',
        completed.id,
        `Completou caça-palavras: ${game.title} em ${formatGameTime(elapsed)}`,
        undefined,
        { time_seconds: elapsed, word_order: order, game_id: game.id, status: 'completed' }
      )
    } catch (err: unknown) {
      setResultSaved(false)
      setError(err instanceof Error ? err.message : 'Erro ao salvar resultado')
    } finally {
      await loadLeaderboard(game.id)
    }
  }

  const markWordFound = (word: string, path: Array<[number, number]>) => {
    if (foundWordsRef.current[word]) return

    const questionIndex = questions.findIndex((q) => q.word === word)
    if (questionIndex < 0) return

    const nextFound = { ...foundWordsRef.current, [word]: true }
    const nextOrder = [...foundOrderRef.current, questionIndex + 1]
    foundWordsRef.current = nextFound
    foundOrderRef.current = nextOrder
    setFoundWords(nextFound)
    setFoundOrder(nextOrder)

    const nextCells = new Set(foundCellsRef.current)
    path.forEach(([r, c]) => nextCells.add(`${r},${c}`))
    foundCellsRef.current = nextCells
    setFoundCells(nextCells)

    if (Object.keys(nextFound).length >= totalWords) {
      void finishGame(nextOrder)
    }
  }

  const evaluateSelection = (path: Array<[number, number]>) => {
    if (!grid || path.length === 0) return
    const letters = path.map(([r, c]) => grid[r][c]).join('')
    const reversed = letters.split('').reverse().join('')

    for (const q of questions) {
      if (foundWordsRef.current[q.word]) continue
      if (q.word === letters || q.word === reversed) {
        markWordFound(q.word, path)
        return
      }
    }
  }

  const pointerToCell = (clientX: number, clientY: number): [number, number] | null => {
    const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null
    if (!target?.dataset?.r || !target?.dataset?.c) return null
    if (!target.classList.contains('game-cell')) return null
    return [parseInt(target.dataset.r, 10), parseInt(target.dataset.c, 10)]
  }

  const onPointerDown = (e: React.PointerEvent) => {
    const cell = pointerToCell(e.clientX, e.clientY)
    if (!cell) return
    isSelectingRef.current = true
    selectStartRef.current = cell
    setSelectPath([cell])
    try {
      boardRef.current?.setPointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    e.preventDefault()
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isSelectingRef.current || !selectStartRef.current) return
    const cell = pointerToCell(e.clientX, e.clientY)
    if (!cell) return
    const path = computePath(
      selectStartRef.current[0],
      selectStartRef.current[1],
      cell[0],
      cell[1]
    )
    if (path) setSelectPath(path)
    e.preventDefault()
  }

  const onPointerUp = () => {
    if (!isSelectingRef.current) return
    isSelectingRef.current = false
    const path = selectPath.slice()
    setSelectPath([])
    selectStartRef.current = null
    if (path.length > 0) evaluateSelection(path)
  }

  const shareResult = () => {
    if (!game) return
    const timeMs = existingSession?.time_seconds ?? resultMs
    const order =
      existingSession?.word_order?.length ? existingSession.word_order : resultOrder
    const text = [
      `🧩 Caça-Palavras — ${game.title}`,
      `⏱️ Tempo: ${formatGameTime(timeMs)}`,
      '🔢 Ordem que encontrei as palavras:',
      buildOrderSquares(order),
    ].join('\n')
    onShareResult?.(text)
  }

  const selectPathSet = new Set(selectPath.map(([r, c]) => `${r},${c}`))

  if (screen === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (screen === 'empty') {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
        <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
          Nenhum jogo disponível
        </Heading>
        <Text size="sm" color="muted" className="mt-2">
          {error ||
            'Não há caça-palavras ativo no período atual. Cadastre um jogo com data válida na aba Cadastrar.'}
        </Text>
      </div>
    )
  }

  const renderClues = (interactive: boolean) => (
    <ol className="space-y-2">
      {questions.map((q, idx) => {
        const found = !!foundWords[q.word]
        return (
          <li
            key={`${q.word}-${idx}`}
            className={`flex gap-3 rounded-lg border px-3 py-2 text-sm transition ${
              found
                ? 'border-teal-300 bg-teal-50 opacity-80 dark:border-teal-700 dark:bg-teal-900/30'
                : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900/40'
            }`}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#008C9A] text-[11px] font-bold text-white">
              {idx + 1}
            </span>
            <span className="min-w-0">
              <span
                className={`text-gray-800 dark:text-gray-100 ${found ? 'line-through decoration-[#016B76]' : ''}`}
              >
                {q.clue}
              </span>
              {interactive && found && (
                <span className="mt-0.5 block text-[11px] font-bold tracking-wide text-[#016B76] dark:text-teal-300">
                  Palavra: {q.word}
                </span>
              )}
            </span>
          </li>
        )
      })}
    </ol>
  )

  const renderLeaderboard = () => {
    if (leaderboard.length === 0) {
      return (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Ainda não há partidas registradas.
        </p>
      )
    }
    let completedRank = 0
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200 dark:border-gray-600">
              <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
                #
              </th>
              <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
                Nome
              </th>
              <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-2 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
                Tempo
                <span className="block text-[9px] font-normal normal-case tracking-normal">mm:ss:mmm</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.slice(0, 20).map((entry) => {
              const isMe = entry.user_id === user?.id
              const isCompleted = entry.status === 'completed'
              if (isCompleted) completedRank += 1
              return (
                <tr
                  key={entry.id}
                  className={`border-b border-gray-100 dark:border-gray-700 ${
                    isMe ? 'bg-teal-50 font-semibold text-[#016B76] dark:bg-teal-900/30 dark:text-teal-200' : ''
                  }`}
                >
                  <td className="px-2 py-2.5 font-extrabold text-[#016B76] dark:text-teal-300">
                    {isCompleted ? rankingPlaceLabel(completedRank) : '—'}
                  </td>
                  <td className="px-2 py-2.5 text-gray-800 dark:text-gray-100">
                    {entry.user_name}
                    {isMe ? ' (você)' : ''}
                  </td>
                  <td className="px-2 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isCompleted
                          ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                      }`}
                    >
                      {isCompleted ? 'Concluiu' : 'Desistiu'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2.5 font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {entry.time_seconds != null ? formatGameTime(entry.time_seconds) : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  if (screen === 'abandoned') {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-8">
        <div className="mb-5 text-center">
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Tentativa encerrada
          </Heading>
          <Text size="sm" color="muted" className="mt-2">
            Você iniciou o caça-palavras e não concluiu. Como há apenas uma tentativa por usuário,
            não é possível jogar novamente.
          </Text>
        </div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
          Ranking
        </p>
        {renderLeaderboard()}
      </div>
    )
  }

  if (screen === 'played' && existingSession) {
    return (
      <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-8">
        <div className="mb-5 text-center">
          <Text size="sm" color="muted">
            Você já jogou este caça-palavras. Seu tempo foi
          </Text>
          <div className="mt-1 text-4xl font-extrabold tabular-nums text-[#016B76] dark:text-teal-300">
            {formatGameTime(existingSession.time_seconds ?? resultMs)}
          </div>
          {(existingSession.word_order?.length || resultOrder.length) > 0 && (
            <Text size="sm" color="muted" className="mt-2">
              Ordem: {buildOrderSquares(existingSession.word_order?.length ? existingSession.word_order : resultOrder)}
            </Text>
          )}
        </div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
          Melhores tempos
        </p>
        {renderLeaderboard()}
        <Button variant="outline" className="mt-4 w-full" onClick={shareResult}>
          Compartilhar resultado
        </Button>
        <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Cada usuário só pode jogar uma vez por edição.
        </p>
      </div>
    )
  }

  if (screen === 'result') {
    return (
      <div
        id="caca-palavras-resultado"
        className="mx-auto max-w-2xl rounded-xl border border-teal-200 bg-white p-6 shadow-sm dark:border-teal-800 dark:bg-gray-800 sm:p-8"
      >
        <div className="mb-5 rounded-xl border border-teal-300 bg-teal-50 px-4 py-5 text-center dark:border-teal-700 dark:bg-teal-900/30">
          <p className="text-lg font-extrabold text-[#016B76] dark:text-teal-200 sm:text-xl">
            Jogo concluído!
          </p>
          <Text size="sm" color="muted" className="mt-2">
            Parabéns! Você encontrou todas as {totalWords} palavras do caça-palavras.
          </Text>
          <p className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-300">
            Seu tempo
          </p>
          <div className="mt-1 text-4xl font-extrabold tabular-nums text-[#016B76] dark:text-teal-300">
            {formatGameTime(resultMs)}
          </div>
          {resultOrder.length > 0 && (
            <Text size="sm" color="muted" className="mt-2">
              Ordem: {buildOrderSquares(resultOrder)}
            </Text>
          )}
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            {error}
          </div>
        )}
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
          Melhores tempos
        </p>
        {renderLeaderboard()}
        <Button variant="outline" className="mt-4 w-full" onClick={shareResult}>
          Compartilhar resultado
        </Button>
        {resultSaved && (
          <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
            Você já registrou sua jogada — este jogo só pode ser feito uma vez por usuário.
          </p>
        )}
      </div>
    )
  }

  if (screen === 'game' && grid) {
    const allFound = totalWords > 0 && foundCount >= totalWords
    return (
      <div className="relative mx-auto w-full max-w-6xl">
        {allFound && (
          <div
            id="caca-palavras-resultado"
            className="mb-4 rounded-xl border border-teal-300 bg-teal-50 px-4 py-4 text-center dark:border-teal-700 dark:bg-teal-900/30"
            role="status"
          >
            <p className="text-lg font-extrabold text-[#016B76] dark:text-teal-200">
              Jogo concluído!
            </p>
            <Text size="sm" color="muted" className="mt-1">
              Parabéns! Você encontrou todas as {totalWords} palavras.
            </Text>
          </div>
        )}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(260px,1fr)_minmax(0,1.4fr)] lg:items-start">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
              Dicas
            </p>
            <div className="mt-2 max-h-[min(65vh,560px)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50/80 p-2 pr-1 dark:border-gray-600 dark:bg-gray-900/40 [scrollbar-gutter:stable]">
              {renderClues(true)}
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex justify-center overflow-x-auto p-1">
              <div
                ref={boardRef}
                className="grid touch-none select-none overflow-hidden rounded-[10px] border-2 border-[#008C9A]"
                style={{
                  gridTemplateColumns: `repeat(${BOARD_SIZE}, min(36px, 5.8vw))`,
                  gridTemplateRows: `repeat(${BOARD_SIZE}, min(36px, 5.8vw))`,
                  gap: '1px',
                  background: '#DCE7E8',
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
              >
                {grid.map((row, r) =>
                  row.map((letter, c) => {
                    const key = `${r},${c}`
                    const isFound = foundCells.has(key)
                    const isSelecting = selectPathSet.has(key) && !isFound
                    return (
                      <div
                        key={key}
                        data-r={r}
                        data-c={c}
                        className={`game-cell flex items-center justify-center font-bold ${
                          isFound
                            ? 'bg-[#CFF1F0] text-[#016B76] shadow-[inset_0_0_0_2px_#01AAB3]'
                            : isSelecting
                              ? 'bg-[#FFD873] shadow-[inset_0_0_0_2px_#E7A900]'
                              : 'bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100'
                        }`}
                        style={{ fontSize: 'calc(min(36px, 5.8vw) * 0.5)', cursor: 'pointer' }}
                      >
                        {letter}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
            <p className="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">
              Arraste sobre as letras para marcar cada palavra (horizontal, vertical ou diagonal).
            </p>
          </div>
        </div>
      </div>
    )
  }

  // start screen
  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-8">
      <Heading as="h2" size="2xl" className="text-center text-gray-900 dark:text-white">
        {game?.title || 'Caça-Palavras'}
      </Heading>
      {game?.description && (
        <Text size="sm" color="muted" className="mt-1 text-center">
          {game.description}
        </Text>
      )}

      <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-center dark:border-red-700 dark:bg-red-900/25">
        <p className="text-sm font-bold text-red-900 dark:text-red-100">
          Atenção: você tem apenas uma única tentativa.
        </p>
        <p className="mt-1 text-xs text-red-800 dark:text-red-200">
          Se fechar a tela ou sair sem concluir, a jogada será encerrada e não poderá tentar de novo.
        </p>
      </div>

      <Text size="sm" color="muted" className="mt-4 text-center leading-relaxed">
        Encontre as {totalWords} palavras escondidas no caça-palavras usando as dicas abaixo. Clique e
        arraste sobre as letras na horizontal, vertical ou diagonal. O cronômetro começa ao clicar em
        Iniciar.
      </Text>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <p className="mt-5 text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
        Dicas
      </p>
      {renderClues(false)}

      <Button
        variant="solid"
        className="mt-6 w-full"
        onClick={() => {
          const ok = window.confirm(
            'Atenção: você tem apenas uma tentativa.\n\nSe sair ou fechar sem concluir, não poderá jogar de novo.\n\nDeseja iniciar agora?'
          )
          if (!ok) return
          void beginGame()
        }}
        disabled={starting || totalWords < 1}
      >
        {starting ? 'Preparando...' : 'Iniciar jogo (1 tentativa)'}
      </Button>
    </div>
  )
}

export default GameJogarTab
